import { useState, useEffect, useCallback, useRef } from 'react';
import { MalformedResponseError } from '../../../apiShape.js';
import { toUserFacingMessage } from '../../../userFacingError.js';
/** A usable schema is an object with a `fields` ARRAY. Zero fields is valid. */
function isProjectSchema(value) {
    return !!value && typeof value === 'object' && Array.isArray(value.fields);
}
/**
 * Fetch the field schema for a provider's project/team/database.
 * Failure-open: on error, schema is null and `error` is set so the caller can
 * fall back to the bare export flow. A request-id guard discards stale resolves
 * after `projectId` (or `opts`) changes.
 */
export function useProjectSchema(runtime, provider, projectId, opts) {
    const { api: integrationsApi, debug } = runtime;
    const [schema, setSchema] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestIdRef = useRef(0);
    const optsKey = opts ? JSON.stringify(opts) : '';
    const load = useCallback(() => {
        if (!projectId) {
            setSchema(null);
            setError(null);
            setIsLoading(false);
            return;
        }
        const reqId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);
        integrationsApi
            .getProjectSchema(provider, projectId, opts)
            .then((result) => {
            if (reqId !== requestIdRef.current)
                return;
            // Failure-open means failure-open on SHAPE too. ExportModal runs
            // `schema.fields.some(...)` during render, so a 200 with a non-schema
            // body used to escape as a render-time TypeError and crash the whole
            // sidebar (latent finding, 2026-07-24 error-path sweep).
            if (!isProjectSchema(result)) {
                throw new MalformedResponseError('the export fields');
            }
            setSchema(result);
            setIsLoading(false);
        })
            .catch((e) => {
            if (reqId !== requestIdRef.current)
                return;
            debug.error('schema fetch failed', e);
            setSchema(null);
            setError(toUserFacingMessage(e, 'Failed to load fields'));
            setIsLoading(false);
        });
    }, [provider, projectId, optsKey, runtime]);
    useEffect(() => { load(); }, [load]);
    return { schema, isLoading, error, refetch: load };
}
//# sourceMappingURL=useProjectSchema.js.map